import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useSettings } from "@/hooks/useSettings";
import { useMetaTags } from "@/hooks/useMetaTags";
import { useAlbumNavigation } from "@/hooks/useAlbumNavigation";
import { useTranslation } from "@/hooks/useTranslation";
import UserActions from "@/components/UserActions";
import { optimizeCloudinaryUrl } from "@/lib/utils";
import { getHomeHeadingOpticalOffset } from "@/lib/googleFonts";
import { Camera, Mail, UserPen } from "lucide-react";

const Home = () => {
  const { user } = useAuth();
  const { getSetting, getBooleanSetting } = useSettings();
  const { getAlbumPath } = useAlbumNavigation();
  const { t } = useTranslation();
  const coverUrl = getSetting('image_cover');
  const bloomdateUrl = (getSetting('bloomdate_url', '') || '').trim();
  const homeHeadingText = getSetting('text_home_h1', t('digitalAlbum'));
  const optimizedCoverUrl = coverUrl && optimizeCloudinaryUrl({ originalUrl: coverUrl, width: 1900 })
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const [homeHeadingShiftY, setHomeHeadingShiftY] = useState('0em');

  useEffect(() => {
    const updateHeadingOffset = () => {
      if (!headingRef.current) return;
      const computedFontFamily = window.getComputedStyle(headingRef.current).fontFamily;
      setHomeHeadingShiftY(getHomeHeadingOpticalOffset(computedFontFamily));
    };

    updateHeadingOffset();

    if (document.fonts?.ready) {
      void document.fonts.ready.then(updateHeadingOffset);
    }
  }, [homeHeadingText]);

  useMetaTags({ pathname: '/inicio' });

  return (
    <div
      className="min-h-screen bg-background px-6 py-8 relative"
      style={optimizedCoverUrl ? {
        backgroundImage: `url(${optimizedCoverUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'top',
        backgroundRepeat: 'no-repeat'
      } : undefined}
    >


      {/* Floating top right container */}
      {user && (
        <div className="absolute top-5 right-5 z-20">
          <UserActions />
        </div>
      )}

      {bloomdateUrl && (
        <a
          href={bloomdateUrl}
          className="absolute top-5 left-5 z-20 inline-flex items-center gap-2 rounded-full border border-border/50 bg-background/75 px-4 py-2 text-sm font-medium text-foreground shadow-sm backdrop-blur-sm transition hover:bg-background/85"
        >
          <Mail className="h-4 w-4" />
          {t('invitation')}
        </a>
      )}


      {/* Welcome container pinned to bottom center */}
      <div
        className="absolute left-1/2 -translate-x-1/2 z-20 w-[92vw] max-w-md"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5rem)' }}>
        <div id="welcome-container" className="w-full bg-background/70 backdrop-blur-sm rounded-3xl p-8 space-y-4 text-center animate-fade-in" style={{ animationDuration: '200ms', animationFillMode: 'both' }}>

          <h1
            ref={headingRef}
            className="pointer-events-none select-none text-4xl md:text-6xl font-light text-foreground leading-tight"
            style={{
              paddingTop: 'var(--heading-top-offset, 0px)',
              transform: `translateY(${homeHeadingShiftY})`,
              willChange: 'transform',
            }}
          >
            {homeHeadingText}
          </h1>
          <p className="text-xl md:text-2xl font-light text-foreground leading-tight">
            {getSetting('text_home_description', '')}
          </p>

          <div className="space-y-4">
            {user ? (
              <>

                <Link to={getAlbumPath("/album")} className="block mx-auto w-full max-w-[230px]">
                  <Button
                    size="lg"
                    className="w-full h-14 text-md font-medium bg-primary hover:bg-secondary text-primary-foreground rounded-xl transition-colors shadow-sm hover:shadow-md"
                  >
                    <Camera
                      strokeWidth={2}
                      style={{ transform: 'scale(1.5)', marginRight: '0.5rem' }}
                    />
                    {t('viewAlbum')}
                  </Button>
                </Link>

                {getBooleanSetting('enable_guestbook', false) && (
                  <Link to={getAlbumPath("/mensajes")} className="block mx-auto w-full max-w-[230px]">
                    <Button
                      size="lg"
                      className="w-full h-14 text-md font-medium bg-primary hover:bg-secondary text-primary-foreground rounded-xl transition-colors shadow-sm hover:shadow-md"
                    >
                      <UserPen
                        strokeWidth={2}
                        style={{ transform: 'scale(1.5)', marginRight: '0.5rem' }}
                      />
                      {t('viewGuestbook')}
                    </Button>
                  </Link>
                )}

              </>
            ) : (
              <Link to={getAlbumPath("/login")} className="block mx-auto w-full max-w-[230px]">
                <Button
                  size="lg"
                  className="w-full h-14 text-lg font-medium bg-primary hover:bg-secondary text-primary-foreground rounded-xl transition-colors"
                >
                  {t('access')}
                </Button>
              </Link>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default Home;
